import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { Nullable } from 'primeng/ts-helpers';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private http: HttpClient = inject(HttpClient);
  private storageService = inject(StorageService);

  private baseUrl = environment.apiUrl;

  private request<T>(
    alternativeBaseUrl: string | undefined = undefined,
    method: string,
    url: string,
    body: any,
    options?: any,
  ): Observable<T> {
    const token = this.storageService.token();

    if (token) {
      options = {
        ...options,
        headers: {
          ...options?.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    }

    var request$ = this.http.request<T>(
      method,
      `${alternativeBaseUrl ?? this.baseUrl}/${url}`,
      {
        body: body,
        headers: {
          ...options?.headers,
        },
        context: options?.context,
        reportProgress: false,
        observe: 'response',
        params: options?.params,
        responseType: options?.responseType ?? 'json',
        withCredentials: true,
      },
    );

    // based on request$, return an observable that emits the body of the response:
    return request$.pipe(
      map((res: HttpResponse<T>) => {
        return res.body as T;
      }),
    );
  }

  get<T>(url: string, options?: any): Observable<T> {
    return this.request<T>(undefined, 'GET', url, null, options);
  }

  post<T>(
    url: string,
    data: any,
    options?: any,
    submitFormData: boolean = false,
    alternativeBaseUrl: string | undefined = undefined,
  ): Observable<T> {
    options = options ?? {};

    // if uploadAttachment is true, we need to handle the request differently
    // because we need to send the data as FormData:
    if (submitFormData) {
      // convert data to FormData:
      const formData = new FormData();
      for (const key of Object.keys(data)) {
        // PBI #2204: null/undefined muessen als fehlendes Feld behandelt
        // werden, nicht als literaler String "null"/"undefined" - genau das
        // tut FormData.append() sonst per Spezifikation (siehe IST-Doku
        // 6.22, Nachfund waehrend der Verifikation).
        if (data[key] === null || data[key] === undefined) {
          continue;
        }
        if (data[key] instanceof File) {
          formData.append(key, data[key], data[key].name);
        } else {
          formData.append(key, data[key]);
        }
      }
      data = formData;
    }

    return this.request<T>(alternativeBaseUrl, 'POST', url, data, options);
  }

  delete<T>(url: string, options?: any): Observable<T> {
    return this.request<T>(undefined, 'DELETE', url, null, options);
  }

  downloadFile(path: string, options?: any): Observable<Nullable<File>> {
    const url = `${this.baseUrl}/${path}`;

    const token = this.storageService.token();

    if (token) {
      options = {
        ...options,
        headers: {
          ...options?.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    }

    var request$ = this.http.get(url, {
      headers: options?.headers,
      responseType: 'blob',
      observe: 'response',
      params: options?.params,
    });

    // based on request$, return an observable that emits the body of the response:
    return request$.pipe(
      map((res: HttpResponse<Blob>) => {
        if (res.body) {
          // assuming that res.headers.get("Content-Disposition") will
          // produce this output: 'attachment; filename="FILENAME.JPG"'
          const contentDisposition = res.headers.get('Content-Disposition');
          const fileName =
            contentDisposition?.split(';')[1].split('=')[1].replace(/"/g, '') ??
            '';
          return new File([res.body as Blob], fileName);
        }

        return null;
      }),
    );
  }
}
